import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import StarField from "@/components/StarField";
import TopBar from "@/components/TopBar";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Register() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const { register, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const intent = params.get("intent"); // "membership" | null
  const isMembership = intent === "membership";

  // ONLY members and influencers get accounts. Registering must always be
  // bound to a membership checkout — otherwise redirect to the membership intent.
  useEffect(() => {
    if (!intent) {
      setParams({ intent: "membership" }, { replace: true });
    }
  }, [intent, setParams]);

  // If user is already logged in and clicked "Become a Member", skip the form entirely
  useEffect(() => {
    if (authLoading || !user) return;
    if (!isMembership) return;
    if (user.role !== "user") { navigate("/dashboard"); return; }
    if (user.membership_active) {
      toast.success("You're already in the Inner Circle");
      navigate("/dashboard");
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/checkout/membership", {
          origin_url: window.location.origin,
        });
        window.location.href = data.url;
      } catch (err) {
        toast.error(err.response?.data?.detail || "Checkout failed");
        navigate("/membership");
      }
    })();
  }, [user, authLoading, isMembership, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(email, password, name);

      if (isMembership) {
        toast.success("Account created. Opening checkout…");
        try {
          const { data } = await api.post("/checkout/membership", {
            origin_url: window.location.origin,
          });
          window.location.href = data.url;
          return; // Prevent falling through
        } catch (err) {
          toast.error(err.response?.data?.detail || "Checkout failed. Try again from your dashboard.");
          navigate("/membership");
          return;
        }
      }

      toast.success("Welcome to the void");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen">
      <StarField count={120} />
      <TopBar />
      <main className="relative z-20 flex items-center justify-center px-6 py-16">
        <div className="glass-strong rounded-sm p-8 sm:p-12 w-full max-w-md">
          <div className="text-center">
            <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-cyan-400/80 mb-2">
              — {isMembership ? "become a disciple" : "request entry"} —
            </div>
            <h1 className="font-display text-3xl font-black uppercase">
              {isMembership ? "Join Inner Circle" : "Create Account"}
            </h1>
            {isMembership && (
              <p className="font-mono text-[11px] uppercase tracking-widest text-white/50 mt-4">
                $10 / month · 15% off events · early access · members-only drops
              </p>
            )}
          </div>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" data-testid="register-name-input" className="w-full px-4 py-3 bg-black/60 border border-white/15 rounded-sm font-mono text-sm focus:outline-none focus:border-cyan-400/80" />
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" data-testid="register-email-input" className="w-full px-4 py-3 bg-black/60 border border-white/15 rounded-sm font-mono text-sm focus:outline-none focus:border-cyan-400/80" />
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password (min 6)" minLength={6} data-testid="register-password-input" className="w-full px-4 py-3 bg-black/60 border border-white/15 rounded-sm font-mono text-sm focus:outline-none focus:border-cyan-400/80" />
            <button type="submit" disabled={loading} data-testid="register-submit-btn" className="gate w-full py-3 bg-white text-black font-display font-bold uppercase tracking-[0.3em] text-xs rounded-sm hover:scale-[1.02] transition disabled:opacity-50">
              {loading ? "..." : isMembership ? "Create Account & Pay $10" : "Begin"}
            </button>
            {isMembership && (
              <p className="text-center font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mt-2">
                You'll be redirected to Stripe to complete payment.
              </p>
            )}
          </form>
          <div className="text-center font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mt-6">
            <Link to={isMembership ? "/login?intent=membership" : "/login"} className="text-cyan-400 hover:underline" data-testid="register-go-login">Already have access?</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
